import axios from "axios";
import store from "../store";
import { setAuthInfo, setLoginStatus } from "../store/reducers/auth-reducer";

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

export function setupAxiosInterceptors() {
  // Request interceptor: attach Authorization header if not already present
  axios.interceptors.request.use(
    (config) => {
      try {
        const authInfoStr = localStorage.getItem("authInfo");
        if (authInfoStr) {
          const authInfo = JSON.parse(authInfoStr);
          if (authInfo && authInfo.token && !config.headers["Authorization"]) {
            config.headers["Authorization"] = `Bearer ${authInfo.token}`;
          }
        }
      } catch (err) {
        console.error("Error reading auth token in request interceptor:", err);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Response interceptor: handle 401 with automatic token refresh & queueing
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (!originalRequest) {
        return Promise.reject(error);
      }

      const url = originalRequest.url || "";
      const isAuthUrl =
        url.includes("/login") ||
        url.includes("/signup") ||
        url.includes("/refresh-token") ||
        url.includes("/logout") ||
        url.includes("/forgot-password") ||
        url.includes("/reset-password");

      if (error.response && error.response.status === 401 && !isAuthUrl) {
        if (originalRequest._retry) {
          return Promise.reject(error);
        }

        let authInfo = null;
        try {
          const raw = localStorage.getItem("authInfo");
          if (raw) authInfo = JSON.parse(raw);
        } catch (e) {
          authInfo = null;
        }

        if (!authInfo || !authInfo.refreshToken) {
          return Promise.reject(error);
        }

        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers["Authorization"] = `Bearer ${token}`;
              return axios(originalRequest);
            })
            .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const role = (authInfo.role || "user").toLowerCase();
        let refreshEndpoint = "/user/refresh-token";
        if (role === "admin") {
          refreshEndpoint = "/admin/refresh-token";
        } else if (role === "seller") {
          refreshEndpoint = "/seller/refresh-token";
        }

        try {
          const refreshRes = await axios.post(
            refreshEndpoint,
            { refreshToken: authInfo.refreshToken },
            { _retry: true }
          );

          const tokenData = refreshRes.data && refreshRes.data.data;
          if (tokenData && tokenData.token) {
            authInfo.token = tokenData.token;
            if (tokenData.refreshToken) {
              authInfo.refreshToken = tokenData.refreshToken;
            }
            if (tokenData.expTime) {
              authInfo.expTime = tokenData.expTime;
            }

            localStorage.setItem("authInfo", JSON.stringify(authInfo));
            try {
              store.dispatch(setAuthInfo({ authInfo }));
            } catch (dispatchErr) {
              console.warn("Redux dispatch setAuthInfo error:", dispatchErr);
            }

            processQueue(null, tokenData.token);
            originalRequest.headers["Authorization"] = `Bearer ${tokenData.token}`;
            return axios(originalRequest);
          } else {
            throw new Error("Invalid token refresh payload");
          }
        } catch (refreshErr) {
          processQueue(refreshErr, null);
          localStorage.removeItem("authInfo");
          localStorage.removeItem("userInfo");
          try {
            store.dispatch(setLoginStatus({ isLoggedIn: false }));
          } catch (dispatchErr) {
            console.warn("Redux dispatch setLoginStatus error:", dispatchErr);
          }
          if (typeof window !== "undefined") {
            if (role === "admin") {
              window.location.href = "/admin/login";
            } else if (role === "seller") {
              window.location.href = "/seller/login";
            } else {
              window.location.href = "/login";
            }
          }
          return Promise.reject(refreshErr);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
}

export default setupAxiosInterceptors;
